import { useEffect, useState } from 'react';
import classNames from 'classnames';

import { useAppDispatch, useAppSelector } from '@/state/store';
import { setIsLocalePickerPanelVisible } from '@/state/features/app';
import { useLang } from '@/components/TranslatedText';
import Flag from '@/components/Flag';
import { getSavedLangIso, saveLangIso } from '@/utils/localLang';
import { DEFAULT_LANG, IS_DISCORD_ACTIVITY } from '@/globals';
import { sendEventV2 } from '@/utils/analytics';
import { isSmallMobileWidth } from '@/utils/mobile';

export const LOCALE_PICKER_HEIGHT = 70;
export const LOCALE_PICKER_ANIM_DURATION = 300;

import * as styles from './styles.module.scss';

export default function LocalePickerPanel() {
    const dispatch = useAppDispatch();
    const isVisible = useAppSelector((state) => state.app.isLocalePickerPanelVisible);
    const { langs, current, setLang } = useLang();
    const [hasCheckedInitial, setHasCheckedInitial] = useState(false);
    const [sortCurrent, setSortCurrent] = useState(current);

    useEffect(() => {
        if (isVisible) {
            // Set the current lang (used for sorting) only after picker has animated closed
            setSortCurrent(current);
        } else {
            const t = setTimeout(() => setSortCurrent(current), LOCALE_PICKER_ANIM_DURATION + 100);
            return () => clearTimeout(t);
        }
    }, [isVisible, current]);

    // Show picker automatically on first visit if translations enabled and no saved preference
    useEffect(() => {
        if (hasCheckedInitial) return;
        if (process.env.PUBLIC_SUPPORT_TRANSLATIONS !== 'true') return;

        const savedLang = getSavedLangIso();
        if (!savedLang) {
            // Don't open picker initially on Discord
            if (IS_DISCORD_ACTIVITY) return;
            dispatch(setIsLocalePickerPanelVisible(true));
        } else if (savedLang !== current) {
            setLang(savedLang);
        }
        setHasCheckedInitial(true);
    }, [hasCheckedInitial]);

    function handleLocaleClick(langISO: string) {
        setLang(langISO);
        saveLangIso(langISO);
        dispatch(setIsLocalePickerPanelVisible(false));
    }

    useEffect(() => {
        if (current && langs?.length) {
            // If recieve locale not in supported langs (e.g from discord), set locale to default
            const matchingLang = langs.some((lang) => lang.langISO === current);
            if (!matchingLang && current !== DEFAULT_LANG.langISO) {
                console.warn(
                    `${current} not found in list of languages. Setting language to default: ${DEFAULT_LANG.langISO}`,
                );
                setLang(DEFAULT_LANG.langISO);
                return;
            }
            sendEventV2('arise_locale', [current]);
        }
    }, [current, langs]);

    // If no languages, show default lang in picker
    // Sort alphabetically, with current one at front
    const flags = (langs?.length ? langs : [DEFAULT_LANG]).slice().sort((a, b) => {
        if (a.langISO === sortCurrent) return -1;
        if (b.langISO === sortCurrent) return 1;
        return getNativeDisplayName(a.langISO, a.displayName).localeCompare(
            getNativeDisplayName(b.langISO, b.displayName),
        );
    });
    // Disable transition on mobiles to prevent performance issues when animating iFrame height
    return (
        <div
            className={styles.LocalePickerPanel}
            inert={!isVisible ? '' : undefined}
            style={{
                height: LOCALE_PICKER_HEIGHT,
                transform: isVisible ? 'translateY(0)' : 'translateY(100%)',
                transition: isSmallMobileWidth() ? undefined : `transform ${LOCALE_PICKER_ANIM_DURATION}ms ease`,
            }}
        >
            <div className={styles.flagsContainer} role="radiogroup" aria-label="Select Language">
                {flags.map(({ langISO, displayName }) => {
                    const nativeName = getNativeDisplayName(langISO, displayName);
                    const isSelected = langs?.length ? langISO === current : true;
                    return (
                        <button
                            key={langISO}
                            role="radio"
                            aria-checked={isSelected}
                            className={classNames(styles.flagButton, {
                                [styles.isSelected]: isSelected,
                            })}
                            onClick={() => handleLocaleClick(langISO)}
                            aria-label={nativeName}
                        >
                            <Flag countryCode={langISO} className={styles.flag} />
                            <span className={styles.flagLabel}>{nativeName}</span>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

function getNativeDisplayName(langISO: string, fallback: string): string {
    try {
        const name = new Intl.DisplayNames([langISO], { type: 'language', languageDisplay: 'standard' }).of(langISO);
        if (name) return name;
    } catch {
        console.log('[Arise] Could not translate display name');
    }
    return fallback;
}
